import { Controller } from "@hotwired/stimulus"

// Fee radio list: auto-fill amount from selected fee, disable online payment when a fee/other
// is selected, show/hide the "Other" reason field, and lock the amount for "Full balance".
export default class extends Controller {
  static targets = [
    "feeRadio",
    "amount",
    "otherReasonGroup",
    "otherReason",
    "taxWarning",
    "chargeSalesTaxCheckbox",
    "manualTaxWrapper"
  ]
  static values = {
    feeAmounts: Object,
    balanceCents: Number,
    salesTaxGuids: Array,
    pupilHasTaxedPayments: Boolean
  }

  connect() {
    this.syncAll()
  }

  feeChanged() {
    this.syncAll()
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  selectedFeeGuid() {
    const checked = this.feeRadioTargets.find(r => r.checked)
    return checked ? checked.value : null
  }

  selectedIsSalesTaxFee() {
    const guid = this.selectedFeeGuid()
    if (!guid) return false
    const guids = this.hasSalesTaxGuidsValue ? this.salesTaxGuidsValue : []
    return guids.includes(guid)
  }

  syncAll() {
    this.applyAmountFromFee()
    this.toggleOtherReason()
    this.syncAmountFieldState()
    this.syncSalesTaxUx()
  }

  // When the selected fee IS a sales tax fee, charging sales tax on top makes no sense:
  // hide and clear the manual "Charge sales tax" control. Otherwise, surface a soft warning
  // when this student has been taxed before but no tax is being collected on this payment.
  syncSalesTaxUx() {
    const isSalesTaxFee = this.selectedIsSalesTaxFee()

    if (this.hasManualTaxWrapperTarget) {
      this.manualTaxWrapperTarget.style.display = isSalesTaxFee ? "none" : ""
    }
    if (this.hasChargeSalesTaxCheckboxTarget && isSalesTaxFee) {
      this.chargeSalesTaxCheckboxTarget.checked = false
    }

    if (!this.hasTaxWarningTarget) return

    const chargingTax =
      this.hasChargeSalesTaxCheckboxTarget && this.chargeSalesTaxCheckboxTarget.checked
    const shouldWarn =
      !isSalesTaxFee && this.pupilHasTaxedPaymentsValue && !chargingTax

    this.taxWarningTarget.classList.toggle("u-hidden", !shouldWarn)
  }

  applyAmountFromFee() {
    const guid = this.selectedFeeGuid()
    if (!guid || guid === "other" || !this.hasAmountTarget) return

    if (guid === "full_balance") {
      this.amountTarget.value = (this.balanceCentsValue / 100).toFixed(2)
      return
    }

    const amt = this.feeAmountsValue[guid]
    if (amt != null && amt !== "") {
      const parsed = Number(amt)
      this.amountTarget.value = Number.isFinite(parsed) ? parsed.toFixed(2) : amt
    }
  }

  syncAmountFieldState() {
    if (!this.hasAmountTarget) return
    const isFullBalance = this.selectedFeeGuid() === "full_balance"
    // readonly keeps the value in the form submission; disabled would drop it
    this.amountTarget.readOnly = isFullBalance
  }

  toggleOtherReason() {
    if (!this.hasOtherReasonGroupTarget) return
    const isOther = this.selectedFeeGuid() === "other"
    this.otherReasonGroupTarget.style.display = isOther ? "" : "none"
    if (this.hasOtherReasonTarget) {
      this.otherReasonTarget.required = isOther
    }
    if (isOther && this.hasAmountTarget) {
      this.amountTarget.value = ""
    }
  }
}
